import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import {
  usePaginatedPharmacyGoodsReceipts,
  usePharmacySuppliers,
  useGeneratePharmacyInvoiceFromGrn,
  useBulkDeletePharmacyGoodsReceipts,
} from '../../services/httpServices/queries';
import {
  Search,
  Loader2,
  AlertCircle,
  Eye,
  ReceiptText,
  ShoppingCart,
  Trash2,
  X,
} from 'lucide-react';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';

const LIMIT = 20;

const invoiceStyles: Record<string, string> = {
  UNPAID: 'bg-danger/10 text-danger',
  PARTIAL: 'bg-warning/10 text-warning',
  PAID: 'bg-success/10 text-success',
  OVERDUE: 'bg-danger/10 text-danger',
};

export function PharmacyReceipts() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [supplierId, setSupplierId] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = usePaginatedPharmacyGoodsReceipts(page, LIMIT, {
    supplierId: supplierId || undefined,
    search: debouncedSearch.trim() || undefined,
  });
  const { data: suppliersData } = usePharmacySuppliers();
  const generateMutation = useGeneratePharmacyInvoiceFromGrn();
  const bulkDeleteMutation = useBulkDeletePharmacyGoodsReceipts();

  const suppliers = suppliersData?.data || [];
  const receipts = useMemo(() => data?.data || [], [data]);
  const meta = data?.meta;

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [supplierId, debouncedSearch]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(receipts.map((r) => r.id));
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [receipts]);

  const allSelected = receipts.length > 0 && receipts.every((r) => selectedIds.has(r.id));
  const someSelected = receipts.some((r) => selectedIds.has(r.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const everySelected = receipts.length > 0 && receipts.every((r) => prev.has(r.id));
      return everySelected ? new Set() : new Set(receipts.map((r) => r.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleGenerate = async (grnId: string) => {
    try {
      const res = await generateMutation.mutateAsync({ grnId });
      toast.success(`Invoice ${res.data?.invoiceNumber || ''} generated`);
    } catch {
      // handled globally
    }
  };

  const handleConfirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    try {
      const res = await bulkDeleteMutation.mutateAsync(ids);
      const deleted = res.data?.deleted?.length ?? 0;
      const blocked = res.data?.blocked?.length ?? 0;
      if (blocked > 0) {
        toast.warning(
          `Moved ${deleted} receipt(s) to trash. ${blocked} skipped — receipts with an invoice or purchase return cannot be removed.`,
        );
      } else {
        toast.success(`Moved ${deleted} receipt(s) to trash`);
      }
      setSelectedIds(new Set());
    } catch {
      toast.error('Failed to move some goods receipts to trash');
    } finally {
      setBulkConfirmOpen(false);
    }
  };

  return (
    <PharmacyLayout
      title="Goods Receipts"
      description="Every medicine delivery received from a vendor, with its supplier bill"
      action={
        <>
          <button
            onClick={() => navigate('/pharmacy/purchasing-trash')}
            className="px-3 py-2 bg-surface border border-border text-text-muted rounded-lg hover:text-text hover:bg-background transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <Trash2 size={16} />
            Trash
          </button>
          <button
            onClick={() => navigate('/pharmacy/purchases/new')}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2 shadow-sm"
          >
            <ShoppingCart size={16} />
            New Purchase Order
          </button>
        </>
      }
    >
      <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by GRN, supplier or invoice..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-9 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={supplierId}
            onChange={(e) => setSupplierId(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Move to trash
            </button>
            <button
              type="button"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background border-b border-border">
              <tr>
                <th className="px-4 py-3 w-10 text-left">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={receipts.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">GRN Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Supplier</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Received</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Items</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Invoice</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" /> Failed to load goods receipts
                  </td>
                </tr>
              ) : receipts.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-text-muted">
                    {debouncedSearch ? 'No goods receipts match your search.' : 'No goods receipts found.'}
                  </td>
                </tr>
              ) : (
                receipts.map((receipt) => (
                  <tr key={receipt.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(receipt.id)}
                        onChange={() => toggleOne(receipt.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono font-medium text-text">{receipt.grnNumber}</td>
                    <td className="px-4 py-3 text-text">{receipt.supplier?.name || '—'}</td>
                    <td className="px-4 py-3 text-text-muted">
                      {receipt.receivedDate ? new Date(receipt.receivedDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-text-muted">{receipt.itemCount ?? receipt.items?.length ?? 0}</td>
                    <td className="px-4 py-3 text-right font-medium text-text">
                      {Number(receipt.totalAmount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      {receipt.invoice ? (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                            invoiceStyles[receipt.invoice.status] || 'bg-background text-text-muted border border-border'
                          }`}
                        >
                          {receipt.invoice.invoiceNumber}
                        </span>
                      ) : (
                        <span className="text-xs text-text-muted">No invoice</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/pharmacy/receipts/${receipt.id}`)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        {!receipt.invoice && (
                          <button
                            onClick={() => handleGenerate(receipt.id)}
                            disabled={generateMutation.isPending}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-success transition-colors disabled:opacity-50"
                            title="Generate supplier bill"
                          >
                            <ReceiptText size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={meta?.page || page}
          totalPages={meta?.totalPages || 1}
          total={meta?.total || 0}
          limit={LIMIT}
          onPageChange={setPage}
          itemName="goods receipts"
        />
      </div>

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Move ${selectedIds.size} goods receipt${selectedIds.size === 1 ? '' : 's'} to trash?`}
        description="Receipts that already have a supplier invoice or a purchase return are skipped."
        confirmLabel={`Move ${selectedIds.size} to trash`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleConfirmBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </PharmacyLayout>
  );
}
